import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MarketingHeader from "@/components/MarketingHeader";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl,
    )}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data.user);
      toast.success(`Welcome back, ${r.data.user.name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSignIn={false} />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="relative hidden lg:flex flex-col justify-end p-10 border-r border-[#1a1a1a] overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
          <div
            className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/40 to-[#0a0a0a]"
            aria-hidden="true"
          />
          <div className="relative z-10">
            <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight">
              Cold email that<br />ships replies.
            </h1>
            <p className="mt-6 text-neutral-400 text-sm leading-relaxed max-w-md">
              Connect Gmail, build sequences, send at scale, score copy with AI.
              Built for founders running outreach without paying $300/mo.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Inboxes" value="∞" />
              <Stat label="Daily caps" value="Custom" />
              <Stat label="AI Analyzer" value="Sonnet 4.5" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
              Sign in
            </div>
            <h2 className="font-display text-3xl font-semibold mt-3 tracking-tight">
              Welcome back
            </h2>

            <div className="mt-6 inline-flex p-1 rounded-md border border-[#27272a] bg-[#0d0d0d] text-xs font-mono">
              <button
                onClick={() => setTab("email")}
                data-testid="tab-email"
                className={`px-3 py-1.5 rounded uppercase tracking-[0.18em] transition-colors ${
                  tab === "email" ? "bg-white text-black" : "text-neutral-400"
                }`}
              >
                Email
              </button>
              <button
                onClick={() => setTab("google")}
                data-testid="tab-google"
                className={`px-3 py-1.5 rounded uppercase tracking-[0.18em] transition-colors ${
                  tab === "google" ? "bg-white text-black" : "text-neutral-400"
                }`}
              >
                Google
              </button>
            </div>

            {tab === "email" ? (
              <form onSubmit={handleEmailLogin} className="mt-6 space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="login-email-input"
                  required
                  className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  data-testid="login-password-input"
                  required
                  className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit-button"
                  className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors py-3 rounded-md font-medium text-sm disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                  ) : (
                    <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                  )}
                  Sign in
                </button>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-neutral-500">
                    No account?{" "}
                    <Link
                      to="/register"
                      data-testid="link-register"
                      className="text-white hover:underline"
                    >
                      Create one
                    </Link>
                  </p>
                  <Link
                    to="/forgot-password"
                    data-testid="link-forgot-password"
                    className="text-xs text-neutral-400 hover:text-white hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>
            ) : (
              <div className="mt-6">
                <p className="text-neutral-400 text-sm leading-relaxed">
                  Use your Google account. We'll create a workspace if you're new.
                </p>
                <button
                  onClick={handleGoogle}
                  data-testid="login-google-button"
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors py-3 rounded-md font-medium text-sm"
                >
                  Continue with Google
                  <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </div>
            )}

            <p className="mt-6 text-xs text-neutral-600 leading-relaxed">
              By continuing you agree to OutreachOS' terms. No spam - your sending
              happens from your own Gmail account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l border-[#27272a] pl-3">
      <div className="font-mono text-sm">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}
